import React from "react";
import MegaHeader from "../MegaHeader";
import { imageUrl } from "../../utils/imagePath";
import "./Datatram.scss";

const DATATRAM_IMG = (file) => imageUrl("datatram", file);
const EMBED_URL = "https://demo.datatram.ai/log-in";

const challengeItems = [
  {
    text: "Disconnected and siloed data across Shopify, Meta, Google, etc.",
    icon: "Database.svg",
  },
  {
    text: "No real-time visibility into performance or engagement",
    icon: "Eye.svg",
  },
  {
    text: "Manual reporting that drains time and delays decisions",
    icon: "File text.svg",
  },
  {
    text: "No single source of truth for campaigns and analytics",
    icon: "Bar chart-2.svg",
  },
  {
    text: "Blurry attribution across multiple customer touchpoints",
    icon: "Trending down.svg",
  },
  {
    text: "Raw data without clear, actionable insight",
    icon: "Search.svg",
  },
];

const featureItems = [
  {
    title: "Dashboard Overview",
    text: "Consolidated view of performance across data channels, allowing granular clarity through data visualisation.",
    icon: "Dashboard.svg",
  },
  {
    title: "Connected Data Sources",
    text: "Integrate platforms like Shopify, Google Ads, Meta Ads, etc for a consolidated view.",
    icon: "Sources.svg",
  },
  {
    title: "Customer Engagement",
    text: "Users can view, create, and manage marketing campaigns, audience lists, and performance analytics.",
    icon: "Engagement.svg",
  },
  {
    title: "AI-Powered Campaign Creation",
    text: "Generate AI-powered campaigns from your specifications in minutes, not hours.",
    icon: "Campiagn.svg",
  },
  {
    title: "Social Listening Agent",
    text: "Track competitor brand sentiment and engagement across social media platforms.",
    icon: "SocialListening.svg",
  },
  {
    title: "Ask DataTram Chatbot",
    text: "Get instant answers on KPIs, performance, create campaigns, analyse dashboards via screenshots.",
    icon: "Chatbot.svg",
  },
  {
    title: "Customer Segmentation",
    text: "Segment customers into meaningful audience groups to target them with the right marketing strategies.",
    icon: "Customer.svg",
  },
  {
    title: "Custom KPI Notifications",
    text: "Automate alerts and recommendations when performance metrics sway from defined thresholds.",
    icon: "KPI.svg",
  },
];

const impactItems = [
  "Reclaim time by automating reporting and data aggregation",
  "Align every channel under one unified analytics view",
  "Use AI to guide campaign strategy, not just measure it",
  "Move faster with real-time insight into engagement and performance",
];

const logoItems = [
  { name: "Slack", file: "slack.svg" },
  { name: "Motive", file: "Motive.svg" },
  { name: "Features", file: "Features.svg" },
  { name: "Cloud", file: "Cloud.svg" },
  { name: "Tree", file: "Tree.svg" },
];

const Datatram = () => {
  return (
    <>
      <MegaHeader />

      <section className="datatram-hero">
        <div
          className="datatram-hero__background"
          aria-hidden="true"
          style={{ backgroundImage: `url(${DATATRAM_IMG("hero.svg")})` }}
        />
        <div className="datatram-hero__content">
          <h1 className="datatram-hero__title">DataTram</h1>
          <h2 className="datatram-hero__subtitle">Marketing Analytics Platform</h2>
          <p className="datatram-hero__description">
            A single source of truth for AI-powered marketing performance,
            customer engagement, and real campaign ROI to enable fast,
            accurate decision-making.
          </p>
        </div>
      </section>

      <section className="datatram-challenge">
        <div className="datatram-challenge__container">
          <h2 className="datatram-challenge__title">The Challenge</h2>
          <p className="datatram-challenge__intro">
            Marketing teams often rely on multiple platforms to track performance
            across social campaigns, sales, and website analytics, leading to
            fragmented reporting and no clear insight into overall performance.
          </p>

          <div className="datatram-challenge__grid">
            {challengeItems.map((item) => (
              <article key={item.text} className="datatram-challenge-card">
                <div className="datatram-challenge-card__icon-wrap">
                  <img
                    src={DATATRAM_IMG(item.icon)}
                    alt=""
                    aria-hidden="true"
                    className="datatram-challenge-card__icon"
                  />
                </div>
                <p className="datatram-challenge-card__text">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="datatram-solution">
        <div className="datatram-solution__container">
          <div className="datatram-solution__header">
            <h2 className="datatram-solution__title">Solution</h2>
            <p className="datatram-solution__intro">
              DataTram streamlines marketing analytics through data
              unification, automation, and AI-powered engagement tools. The
              platform enables businesses to connect their key data sources,
              visualise metrics through interactive dashboards, and interact
              with their data using natural language queries.
            </p>
          </div>

          <div className="datatram-solution__demo-link-row">
            <a
              href={EMBED_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="datatram-solution__demo-link"
              aria-label="View DataTram demo in a new tab"
            >
              <span>View Demo</span>
              <img
                src={DATATRAM_IMG("ViewArrow.svg")}
                alt=""
                aria-hidden="true"
                className="datatram-solution__demo-link-icon"
              />
            </a>
          </div>

          <div className="datatram-solution-embed-wrapper">
            <iframe
              src={EMBED_URL}
              title="DataTram Demo"
              className="datatram-iframe"
              allowFullScreen
              allow="fullscreen"
              scrolling="yes"
            />
          </div>
        </div>
      </section>

      <section className="datatram-features">
        <div className="datatram-features__container">
          <h2 className="datatram-features__title">Key Features</h2>
          <div className="datatram-features__grid">
            {featureItems.map((item) => (
              <article key={item.title} className="datatram-feature-card">
                <div className="datatram-feature-card__icon-wrap">
                  <img
                    src={DATATRAM_IMG(item.icon)}
                    alt=""
                    aria-hidden="true"
                    className="datatram-feature-card__icon"
                  />
                </div>
                <div className="datatram-feature-card__content">
                  <h3 className="datatram-feature-card__title">{item.title}</h3>
                  <p className="datatram-feature-card__text">{item.text}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="datatram-features__logos">
            {logoItems.map((item) => (
              <img
                key={item.name}
                src={DATATRAM_IMG(item.file)}
                alt={item.name}
                className={`datatram-features__logo datatram-features__logo--${item.name.toLowerCase()}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section
        className="datatram-impact"
        style={{ backgroundImage: `url(${DATATRAM_IMG("footer.svg")})` }}
      >
        <div className="datatram-impact__overlay" aria-hidden="true" />
        <div className="datatram-impact__content">
          <h2 className="datatram-impact__title">Impact</h2>
          <ul className="datatram-impact__list">
            {impactItems.map((item) => (
              <li key={item}>
                <span className="datatram-impact__check" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};

export default Datatram;
